import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import { createStatusSubscription } from "../routes/Workspace/statusSubscriptionService";

const TTSFileStatusContext = createContext();

// `process.env.NODE_ENV` is replaced at build time by webpack's DefinePlugin
// (CRA default), so this becomes a literal `true`/`false` in the bundle and
// Terser dead-code-eliminates every `if (TTS_DEBUG) {…}` branch in the
// production build. Logs stay available locally via `npm start`; users never
// pay for them.
export const TTS_DEBUG = process.env.NODE_ENV === "development";

// If a batch's __requestStatus stays PROCESSING for this long with no per-tag
// progress, treat the backend as silent: mark __requestStatus as "STUCK" so
// the icon falls through to DB state (retryableDb) instead of spinning forever.
const STUCK_PROCESSING_MS = 90 * 1000;

const debugLog = (msg) => {
  if (TTS_DEBUG) console.info(msg);
};

const collectPerTagStatuses = (filesMap) =>
  Object.entries(filesMap)
    .filter(
      ([key]) => key !== "__requestStatus" && typeof filesMap[key] === "object",
    )
    .map(([, file]) => file.status);

const allGenerated = (statuses) =>
  statuses.length > 0 && statuses.every((s) => s === "GENERATED");

// __requestStatus="STUCK" means the frontend timed out waiting for activity
// (we never mark STUCK over a GENERATED). If every per-tag we received was
// GENERATED, the backend likely processed a partial rerun successfully and
// just forgot to emit __requestStatus=GENERATED — treat as success.
// Otherwise the backend stalled — surface FAILED so the user can retry.
const resolveStuckBatch = (statuses, totalExpected) => {
  if (allGenerated(statuses)) {
    debugLog(
      `[calculateOverallStatus] → GENERATED (STUCK but ${statuses.length} per-tag GENERATEDs and no FAILEDs)`,
    );
    return TTSTagStatusEnum.GENERATED;
  }
  debugLog(
    `[calculateOverallStatus] → FAILED (STUCK with ${statuses.length}/${totalExpected} received — backend stalled)`,
  );
  return TTSTagStatusEnum.FAILED;
};

// Either a per-tag FAILED or __requestStatus=FAILED was seen.
//   - While the batch is still PROCESSING and not every expected tag has
//     arrived, a per-tag FAILED may be corrected by a later GENERATED
//     (backend streams both attempts on retry). Defer.
//   - Defense-in-depth: ignore stray __requestStatus=FAILED when every
//     per-tag is GENERATED.
//   - Otherwise — surface FAILED.
const resolveFailedBatch = (statuses, requestStatus, totalExpected) => {
  const allReceived = totalExpected > 0 && statuses.length >= totalExpected;
  if (
    requestStatus?.status === "PROCESSING" &&
    !allReceived &&
    statuses.includes("FAILED")
  ) {
    debugLog(
      `[calculateOverallStatus] → PROCESSING (deferring per-tag FAILED until batch settles, ${statuses.length}/${totalExpected})`,
    );
    return TTSTagStatusEnum.PROCESSING;
  }

  if (
    requestStatus?.status === "FAILED" &&
    !statuses.includes("FAILED") &&
    allGenerated(statuses)
  ) {
    debugLog(
      `[calculateOverallStatus] → GENERATED (${statuses.length} tags GENERATED despite stray __requestStatus: FAILED)`,
    );
    return TTSTagStatusEnum.GENERATED;
  }

  debugLog("[calculateOverallStatus] → FAILED");
  return TTSTagStatusEnum.FAILED;
};

// __requestStatus=PROCESSING is the authoritative in-progress signal — but
// the Lambda sometimes forgets to emit GENERATED after every per-tag finishes.
// If we know the totalExpected and every expected tag arrived as GENERATED,
// treat the batch as done.
const resolveProcessingBatch = (statuses, totalExpected) => {
  if (
    totalExpected > 0 &&
    statuses.length >= totalExpected &&
    allGenerated(statuses)
  ) {
    debugLog(
      `[calculateOverallStatus] → GENERATED (all ${totalExpected} tags received despite stuck PROCESSING signal)`,
    );
    return TTSTagStatusEnum.GENERATED;
  }
  debugLog(
    `[calculateOverallStatus] → PROCESSING (waiting: ${statuses.length}/${totalExpected} received)`,
  );
  return TTSTagStatusEnum.PROCESSING;
};

export const calculateOverallStatus = (filesMap, totalExpected = 0) => {
  if (!filesMap) return TTSTagStatusEnum.NOT_STARTED;

  const requestStatus = filesMap["__requestStatus"];
  const statuses = collectPerTagStatuses(filesMap);

  if (TTS_DEBUG) {
    const counts = {
      GENERATED: statuses.filter((s) => s === "GENERATED").length,
      FAILED: statuses.filter((s) => s === "FAILED").length,
      OTHER: statuses.filter((s) => s !== "GENERATED" && s !== "FAILED").length,
    };
    console.info(
      `[calculateOverallStatus] __requestStatus: ${requestStatus?.status ?? "none"}` +
        ` | received: ${statuses.length}/${totalExpected}` +
        ` | statusCounts: ${JSON.stringify(counts)}`,
    );
  }

  if (requestStatus?.status === "STUCK")
    return resolveStuckBatch(statuses, totalExpected);

  if (statuses.includes("FAILED") || requestStatus?.status === "FAILED")
    return resolveFailedBatch(statuses, requestStatus, totalExpected);

  if (requestStatus?.status === "PROCESSING")
    return resolveProcessingBatch(statuses, totalExpected);

  if (requestStatus?.status === "GENERATED") {
    // Trust the backend's terminal signal. Partial reruns are legitimate.
    debugLog("[calculateOverallStatus] → GENERATED (batch signal)");
    return TTSTagStatusEnum.GENERATED;
  }

  // No batch signal yet — fall back to per-tag statuses.
  if (allGenerated(statuses)) {
    debugLog(
      "[calculateOverallStatus] → GENERATED (fallback: all received tags are GENERATED, no batch signal)",
    );
    return TTSTagStatusEnum.GENERATED;
  }

  debugLog(
    `[calculateOverallStatus] → PROCESSING (default, no __requestStatus, ${statuses.length} received)`,
  );
  return TTSTagStatusEnum.PROCESSING;
};

const isTerminalRequestStatus = (status) =>
  status === "GENERATED" || status === "FAILED";

const TERMINAL_BATCH_STATUSES = new Set(["GENERATED", "FAILED", "STUCK"]);

export const isTerminalBatchStatus = (status) =>
  TERMINAL_BATCH_STATUSES.has(status);

const isStaleByTimestamp = (existing, incomingTs) =>
  !!existing && new Date(existing.timestamp) >= new Date(incomingTs);

const writeMapEntry = (prev, requestId, key, value) => ({
  ...prev,
  [requestId]: {
    ...(prev[requestId] || {}),
    [key]: value,
  },
});

// Pure reducer for an inbound WebSocket message. Encapsulates the GENERATED-
// trumps-FAILED rules and the timestamp-based staleness check so the message-
// handling rules can be unit-tested without mounting the provider.
export const applyStatusMessage = (prev, statusMessage) => {
  const { requestId: msgRequestId, tagId, timestamp, status } = statusMessage;
  const key = tagId ?? "__requestStatus";
  const existing = prev[msgRequestId]?.[key];

  // GENERATED is a terminal success — the audio file exists in S3.
  // Ignore any later FAILED for the same key (stray retry/replay events
  // shouldn't revert a previously-successful tag or batch).
  if (existing?.status === "GENERATED" && status === "FAILED") {
    if (TTS_DEBUG) {
      console.info(
        `[TTS WebSocket] ignoring stale FAILED after GENERATED for key: ${key}`,
      );
    }
    return prev;
  }

  // Once the batch __requestStatus is GENERATED, the Lambda has declared the
  // whole request successful. Any per-tag FAILED that arrives afterwards is
  // backend noise (throttle/timeout retries) — do not flip the icon back.
  if (
    key !== "__requestStatus" &&
    status === "FAILED" &&
    prev[msgRequestId]?.["__requestStatus"]?.status === "GENERATED"
  ) {
    if (TTS_DEBUG) {
      console.info(
        `[TTS WebSocket] ignoring per-tag FAILED after batch __requestStatus=GENERATED for key: ${key}`,
      );
    }
    return prev;
  }

  // For __requestStatus, terminal statuses (GENERATED/FAILED) must always
  // override non-terminal ones regardless of timestamp. The Lambda can send
  // both messages within the same second, making timestamp-stale checks
  // incorrectly drop GENERATED.
  if (
    key === "__requestStatus" &&
    isTerminalRequestStatus(status) &&
    existing &&
    !isTerminalRequestStatus(existing.status)
  ) {
    if (TTS_DEBUG) {
      console.info(
        `[TTS WebSocket] __requestStatus priority override: ${existing.status} → ${status}`,
      );
    }
    return writeMapEntry(prev, msgRequestId, key, statusMessage);
  }

  if (isStaleByTimestamp(existing, timestamp)) return prev;
  return writeMapEntry(prev, msgRequestId, key, statusMessage);
};

// Pure reducer for the inactivity-timer firing. Returns the next map (or
// `prev` unchanged) — the timer just dispatches this through the setter.
export const applyInactivityStuck = (prev, msgRequestId, armedTimestampMs) => {
  const reqMap = prev[msgRequestId];
  if (!reqMap) return prev;
  const cur = reqMap["__requestStatus"];
  // Already settled? Nothing to do. GENERATED is terminal — partial reruns
  // are legitimate so we trust the backend's success signal even if not all
  // expected tags arrived.
  if (isTerminalBatchStatus(cur?.status)) return prev;

  // If anything newer than this timer's armed message has arrived for this
  // requestId, the timer is stale — drop it.
  const latestActivityMs = Object.values(reqMap).reduce((max, entry) => {
    const t = entry?.timestamp ? new Date(entry.timestamp).getTime() : 0;
    return t > max ? t : max;
  }, 0);
  if (latestActivityMs > armedTimestampMs) return prev;

  if (TTS_DEBUG) {
    console.info(
      `[TTS WebSocket] inactivity ${STUCK_PROCESSING_MS / 1000}s — marking __requestStatus as STUCK for: ${msgRequestId}`,
    );
  }
  const newRequestStatus = cur
    ? { ...cur, status: "STUCK" }
    : { status: "STUCK", timestamp: new Date().toISOString() };
  return {
    ...prev,
    [msgRequestId]: { ...reqMap, __requestStatus: newRequestStatus },
  };
};

export const TTSTagStatusProvider = ({ children }) => {
  const [ttsTagStatusMap, setTTSTagStatusMap] = useState({});
  const subRef = useRef([]);
  // Track stuck-inactivity timers so we can clear them on unsubscribe and
  // avoid late-fire setState on unmounted/replaced subscriptions.
  const timersRef = useRef(new Set());

  const armStuckTimer = (msgRequestId, armedTimestampMs) => {
    const timeoutId = setTimeout(() => {
      timersRef.current.delete(timeoutId);
      setTTSTagStatusMap((prev) =>
        applyInactivityStuck(prev, msgRequestId, armedTimestampMs),
      );
    }, STUCK_PROCESSING_MS);
    timersRef.current.add(timeoutId);
    return timeoutId;
  };

  const tearDown = () => {
    subRef.current.forEach((sub) => sub?.unsubscribe?.());
    subRef.current = [];
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current.clear();
    setTTSTagStatusMap({});
  };

  const startSubscription = useCallback(
    (client) => (userId) => async (requestIds) => {
      const subscribeToRequest = async (requestId) => {
        try {
          return await createStatusSubscription({
            client,
            requestId,
            userId,
            onMessage: (statusMessage) => {
              if (TTS_DEBUG) {
                const key = statusMessage.tagId ?? "__requestStatus";
                console.info(
                  `[TTS WebSocket] requestId: ${statusMessage.requestId} | key: ${key} | status: ${statusMessage.status}`,
                );
              }
              setTTSTagStatusMap((prev) =>
                applyStatusMessage(prev, statusMessage),
              );
              armStuckTimer(
                statusMessage.requestId,
                new Date(statusMessage.timestamp).getTime(),
              );
            },
            onError: (err) => console.error("Appsync Subscription Error", err),
            onComplete: (data) =>
              console.error("Appsync Subscription onComplete", data),
          });
        } catch (err) {
          console.error("Fail to configure Appsync Subscription:", err);
          return undefined;
        }
      };

      tearDown();
      if (TTS_DEBUG) {
        console.info(
          `[TTS Subscription] Starting for: ${JSON.stringify(Array.isArray(requestIds) ? requestIds : [requestIds])}`,
        );
      }

      const ids = Array.isArray(requestIds) ? requestIds : [requestIds];
      const subs = await Promise.all(ids.map(subscribeToRequest));
      subRef.current = subs.filter(Boolean);

      return tearDown;
    },
    [],
  );

  return (
    <TTSFileStatusContext.Provider
      value={{ ttsTagStatusMap, startSubscription }}
    >
      {children}
    </TTSFileStatusContext.Provider>
  );
};

export const TTSTagStatusDBEnum = Object.freeze({
  PENDING: "pending",
  SAVED: "saved",
  // The backend writes "generated" (not "saved") for a successful tag in
  // `ttsTagStatuses`. Both values represent a terminal-success DB row;
  // SAVED is kept for legacy data paths (clone flow, older migrations).
  GENERATED: "generated",
  FAILED: "failed",
  STUCK: "stuck",
  PROCESSING: "processing",
  ENQUEUED: "enqueued",
});

// Overlay `processing` on the response's ttsTagStatuses for tags we explicitly
// asked the backend to regenerate. The backend often leaves a previously-FAILED
// DB row at `failed` until completion, which short-circuits the per-tag UI to
// the red error icon; without this overlay the user never sees a spinner on the
// tags they just asked to retry. Tags not in retryTagIds are passed through
// untouched. Returns a new array.
export const applyOptimisticRetryStatuses = (responseStatuses, retryTagIds) => {
  if (!retryTagIds?.length) return responseStatuses ?? [];
  const retrySet = new Set(retryTagIds);
  const existing = (responseStatuses ?? []).filter(Boolean);
  const existingIds = new Set(existing.map((entry) => entry.tagId));
  const overlaid = existing.map((entry) =>
    retrySet.has(entry.tagId)
      ? { ...entry, status: TTSTagStatusDBEnum.PROCESSING }
      : entry,
  );
  const appended = retryTagIds
    .filter((tagId) => !existingIds.has(tagId))
    .map((tagId) => ({ tagId, status: TTSTagStatusDBEnum.PROCESSING }));
  return [...overlaid, ...appended];
};

export const hasRetryableDbTags = (dbStatuses, liveMap) =>
  dbStatuses.some((entry) => {
    if (!entry) return false;
    const { tagId, status } = entry;
    const hasLive = Object.keys(liveMap).some(
      (k) => k !== "__requestStatus" && k === tagId,
    );
    return (
      !hasLive &&
      (status === TTSTagStatusDBEnum.FAILED ||
        status === TTSTagStatusDBEnum.STUCK)
    );
  });

export const hasProcessingDbTags = (dbStatuses, liveMap) =>
  dbStatuses.some((entry) => {
    if (!entry) return false;
    const { tagId, status } = entry;
    const hasLive = Object.keys(liveMap).some(
      (k) => k !== "__requestStatus" && k === tagId,
    );
    return (
      !hasLive &&
      (status === TTSTagStatusDBEnum.PENDING ||
        status === TTSTagStatusDBEnum.PROCESSING ||
        status === TTSTagStatusDBEnum.ENQUEUED)
    );
  });

export const getLiveEntryForTag = (filesStatusMap, tagIdentifier) => {
  if (!filesStatusMap || tagIdentifier === "__requestStatus") return undefined;
  const value = filesStatusMap[tagIdentifier];
  return value ? [tagIdentifier, value] : undefined;
};

const ALL_FALSE_STATUS = Object.freeze({
  isTagFailed: false,
  isTagStuck: false,
  isTagProcessing: false,
});

const TERMINAL_DB_STATUSES = {
  [TTSTagStatusDBEnum.FAILED]: { ...ALL_FALSE_STATUS, isTagFailed: true },
  [TTSTagStatusDBEnum.STUCK]: { ...ALL_FALSE_STATUS, isTagStuck: true },
  [TTSTagStatusDBEnum.SAVED]: ALL_FALSE_STATUS,
  [TTSTagStatusDBEnum.GENERATED]: ALL_FALSE_STATUS,
};

const LIVE_PROCESSING_STATUSES = new Set(["PROCESSING", "ENQUEUED"]);

const deriveFromLiveEntry = (liveEntry, deferLiveFailed) => {
  const status = liveEntry[1].status;
  const isLiveFailed = status === "FAILED";
  return {
    isTagFailed: isLiveFailed && !deferLiveFailed,
    isTagStuck: false,
    isTagProcessing:
      LIVE_PROCESSING_STATUSES.has(status) || (isLiveFailed && deferLiveFailed),
  };
};

const deriveFromDbEntry = (dbStatus, batchIsProcessing, batchHasTerminated) => {
  const terminal = TERMINAL_DB_STATUSES[dbStatus];
  if (terminal) return terminal;

  const isDbInFlight =
    dbStatus === TTSTagStatusDBEnum.PENDING ||
    dbStatus === TTSTagStatusDBEnum.PROCESSING ||
    dbStatus === TTSTagStatusDBEnum.ENQUEUED;

  // Batch declared itself done but this tag never got a per-tag event and DB
  // still says it was being worked on — backend silently dropped it.
  if (isDbInFlight && batchHasTerminated) {
    return { ...ALL_FALSE_STATUS, isTagStuck: true };
  }

  return {
    ...ALL_FALSE_STATUS,
    isTagProcessing: isDbInFlight || batchIsProcessing,
  };
};

// Derive the panel's per-tag UI state. Live (WebSocket) result wins when
// present; otherwise we fall back to the DB row, with two important rules:
//   - A terminal DB status (saved/failed/stuck) survives an in-flight batch.
//   - A DB-in-flight tag that the batch never delivered graduates to STUCK
//     once the batch itself reaches a terminal __requestStatus.
export const deriveTagStatus = ({
  liveEntry,
  dbEntry,
  deferLiveFailed,
  batchIsProcessing,
  batchHasTerminated = false,
}) =>
  liveEntry
    ? deriveFromLiveEntry(liveEntry, deferLiveFailed)
    : deriveFromDbEntry(dbEntry?.status, batchIsProcessing, batchHasTerminated);

export const TTSTagStatusEnum = Object.freeze({
  NOT_STARTED: "NOT_STARTED",
  ENQUEUED: "ENQUEUED",
  PROCESSING: "PROCESSING",
  FAILED: "FAILED",
  GENERATED: "GENERATED",
});

export const useTTSFileStatusContext = () => {
  const context = useContext(TTSFileStatusContext);
  if (!context) {
    throw new Error(
      "useTTSFileStatusContext must be used within a TTSFileStatusProvider",
    );
  }
  return context;
};
